import { Injectable, Injector, OnDestroy, Type } from '@angular/core';
import { ActivatedRouteSnapshot, NavigationEnd, Router } from '@angular/router';
import {
  BehaviorSubject,
  concat,
  from,
  isObservable,
  Observable,
  of,
  Subscription,
} from 'rxjs';
import {
  concatMap,
  filter,
  first,
  mergeMap,
  tap,
  toArray,
  withLatestFrom,
} from 'rxjs/operators';
import { Breadcrumb } from './breadcrumb';
import { BreadcrumbsResolver } from './breadcrumbs.resolver';

@Injectable({
  providedIn: 'root',
})
export class BreadcrumbsService implements OnDestroy {
  postProcess!: (
    crumbs: Breadcrumb[],
  ) => Promise<Breadcrumb[]> | Observable<Breadcrumb[]> | Breadcrumb[];

  protected breadcrumbs = new BehaviorSubject<Breadcrumb[]>([]);
  protected defaultResolver = new BreadcrumbsResolver();
  protected subscription?: Subscription;

  get crumbs$(): BehaviorSubject<Breadcrumb[]> {
    return this.breadcrumbs;
  }

  constructor(
    protected router: Router,
    protected injector: Injector,
  ) {
    this.subscription = this.router.events
      .pipe(
        filter((x) => x instanceof NavigationEnd),
        concatMap(() => this.onNavigationEnd()),
      )
      .subscribe();

    this.onNavigationEnd().subscribe();
  }

  ngOnDestroy() {
    this.subscription?.unsubscribe();
    this.breadcrumbs.complete();
  }

  protected onNavigationEnd() {
    return this.resolveCrumbs(this.router.routerState.snapshot.root).pipe(
      mergeMap((breadcrumb) => breadcrumb),
      toArray(),
      mergeMap((breadcrumbs) =>
        this.postProcess
          ? this.wrapIntoObservable(this.postProcess(breadcrumbs)).pipe(first())
          : of(breadcrumbs),
      ),
      withLatestFrom(this.breadcrumbs),
      tap(([a, b]) => this.combineCrumbs(a, b)),
    );
  }

  combineCrumbs(next: Breadcrumb[], before: Breadcrumb[]) {
    this.breadcrumbs.next(
      next.map((breadcrumb) => {
        const oldBreadcrumb = before.find(
          (oldBreadcrumb) => oldBreadcrumb.key === breadcrumb.key,
        );
        if (oldBreadcrumb) {
          return {
            ...breadcrumb,
            ...oldBreadcrumb,
          };
        }

        return breadcrumb;
      }),
    );
  }

  protected resolveCrumbs(
    route: ActivatedRouteSnapshot,
  ): Observable<Breadcrumb[]> {
    let crumbs$: Observable<Breadcrumb[]> = of([]);
    const data = route.routeConfig?.data;

    if (data?.breadcrumb) {
      const resolver = this.getBreadcrumbResolver(data.breadcrumb);
      const result = resolver.resolve(route, this.router.routerState.snapshot);
      crumbs$ = this.wrapIntoObservable(result).pipe(first());
    }

    return route.firstChild
      ? concat(crumbs$, this.resolveCrumbs(route.firstChild))
      : crumbs$;
  }

  protected getBreadcrumbResolver(
    breadcrumbs: string | Type<BreadcrumbsResolver>,
  ): BreadcrumbsResolver {
    return typeof breadcrumbs === 'function' &&
      breadcrumbs.prototype instanceof BreadcrumbsResolver
      ? this.injector.get<BreadcrumbsResolver>(breadcrumbs)
      : this.defaultResolver;
  }

  protected wrapIntoObservable<T>(
    value: T | Promise<T> | Observable<T>,
  ): Observable<T> {
    return isObservable(value) ? value : from(Promise.resolve(value));
  }

  updateLabel(key: string, value: string): void {
    this.breadcrumbs.next(
      this.breadcrumbs.value.map((crumb) => {
        if (crumb.key === key) {
          crumb.label = value;
        }

        return crumb;
      }),
    );
  }

  updateRoute(key: string, updates: Partial<Omit<Breadcrumb, 'key'>>): void {
    this.breadcrumbs.next(
      this.breadcrumbs.value.map((crumb) => {
        if (crumb.key === key) {
          console.log('[ BreadcrumbsService/updateRoute]', crumb);
          crumb = {
            ...crumb,
            ...updates,
          };
        }

        return crumb;
      }),
    );
  }

  addLabel(item: Breadcrumb): void {
    this.breadcrumbs.value.push(item);
    this.breadcrumbs.next(this.breadcrumbs.value);
  }

  removeLabel(key: string): void {
    const index = this.breadcrumbs.value.findIndex(
      (crumb) => crumb.key === key,
    );
    this.breadcrumbs.value.splice(index, 1);
    this.breadcrumbs.next(this.breadcrumbs.value);
  }
}
